import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useLocation } from "react-router-dom";

// Import API
import FetchData from '../api/Api';

const AppContext = createContext();

export const useApp = () => useContext(AppContext);

export const AppProvider = ({ children }) => {
  
  const location = useLocation();
  const locale = location.pathname.split("/")[1];

  // State management
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [user, setUser] = useState(null);
  const [contact, setContact] = useState(null);
  const [categories, setCategories] = useState(null);
  const [toCart, setToCart] = useState(() => {
    const savedCart = localStorage.getItem("cart");
    return savedCart ? JSON.parse(savedCart) : {
      products: null,
      promo: null,
      paymethod: 'BOG_CARD'
    };
  });
  

  // Sync cart with localStorage
  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(toCart));
  }, [toCart]);

  // Login function
  const login = useCallback((token) => {
    localStorage.setItem('token', token);
    setToken(token);
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    try {
      const fetchDataInstance = new FetchData();
      
      await fetchDataInstance.initHeaders('', token);
      
      const result = await fetchDataInstance.get(`/api/logout`, true);
      
      if (result?.status === 'OK') {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);  // Clear user on logout
      }
    } catch (error) {
      console.error("Logout failed:", error.message);
    }
  }, [token]);

  // Fetch user data if token exists
  useEffect(() => {
    if (token) {
      const fetchUserData = async () => {
        try {
          const fetchDataInstance = new FetchData();
          await fetchDataInstance.initHeaders('', token);
          const result = await fetchDataInstance.get(`/api/user`, true);
          setUser(result);
        } catch (error) {
          console.error("Failed to fetch user data:", error.message);
        }
      };

      fetchUserData();
    } else {
      setUser(null);
    }
  }, [token]);

  // Fetch contact
  useEffect(() => {
    if (contact === null) {
      const fetchContactData = async () => {
        try {
          const fetchDataInstance = new FetchData();
          await fetchDataInstance.initHeaders(locale.length > 0 ? locale : 'ka' );
          const result = await fetchDataInstance.get(`/api/contact`, true);
          setContact(result);
        } catch (error) {
          console.error("Failed to fetch contact data:", error.message);
        }
      };

      fetchContactData();
    }
  }, [contact]);


  // Fetch categories
  useEffect(() => {
    if (categories === null) {
      const fetchCategoriesData = async () => {
        try {
          const fetchDataInstance = new FetchData();
          await fetchDataInstance.initHeaders(locale.length > 0 ? locale : 'ka' );
          const result = await fetchDataInstance.get(`/api/categories`, true);
          setCategories(result);
        } catch (error) {
          console.error("Failed to fetch contact data:", error.message);
        }
      };

      fetchCategoriesData();
    }
  }, [categories]);

  useEffect(()=> {
    setContact(null);
    setCategories(null);
  }, [locale])
  

  // Context value to be provided to children
  const value = {
    user,
    setUser,
    token,
    login,
    logout,
    toCart,
    setToCart,
    contact,
    categories
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
